/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/
import React, { Component } from 'react';
// BASE components
import BrowserRouter from '../@BASE/core/BrowserRouter';
import BASELayout from '../@BASE/core/BASELayout';
import BASETheme from '../@BASE/core/BASETheme';
import BASEAuthorization from '../@BASE/core/BASEAuthorization';
// extra
import { SnackbarProvider } from 'notistack';
import { useSelector } from 'react-redux';
import rtlPlugin from 'stylis-plugin-rtl';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { selectCurrentLanguageDirection } from './store/i18nSlice';
import { selectUser } from './store/userSlice';
import themeLayout from './theme-layouts/themeLayout';
import {
  selectMainTheme,
  selectMainThemeDark
} from './store/BASE/settingsSlice';
import settingsConfig from '../app/configs/settingsConfig';
import withAppProviders from './withAppProviders';
import { AuthProvider } from './auth/AuthContext';

const emotionCacheOptions = {
  rtl: {
    key: 'muirtl',
    stylisPlugins: [rtlPlugin],
    insertionPoint: document.getElementById('emotion-insertion-point')
  },
  ltr: {
    key: 'muiltr',
    stylisPlugins: [],
    insertionPoint: document.getElementById('emotion-insertion-point')
  }
};

// ----------------------------------------------------------------------
const App = () => {
  const user = useSelector(selectUser);
  const langDirection = useSelector(selectCurrentLanguageDirection);
  const mainTheme = useSelector(selectMainTheme);
  const darkTheme = useSelector(selectMainThemeDark);
  const appTheme =
    process.env.NODE_ENV === 'development' ? darkTheme : mainTheme;

  return (
    <CacheProvider value={createCache(emotionCacheOptions[langDirection])}>
      <BASETheme theme={appTheme} direction={langDirection}>
        <AuthProvider>
          <BrowserRouter>
            <BASEAuthorization
              userRole={user.role}
              loginRedirectUrl={settingsConfig.loginRedirectUrl}
            >
              <SnackbarProvider
                maxSnack={5}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right'
                }}
                classes={{
                  containerRoot:
                    'bottom-0 right-0 mb-52 md:mb-68 mr-8 lg:mr-80 z-99'
                }}
              >
                <BASELayout layouts={themeLayout} />
              </SnackbarProvider>
            </BASEAuthorization>
          </BrowserRouter>
        </AuthProvider>
      </BASETheme>
    </CacheProvider>
  );
};

export default withAppProviders(App)();
